import { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import ConfirmModal from '@/domains/dashboard-management/components/ConfirmModal/ConfirmModal';
import ButtonFloat from '@/domains/mobile-web/components/ButtonFloat/ButtonFloat';
import CardDonationPost from '@/domains/mobile-web/components/CardDonationPost/CardDonationPost';
import TabMobile, { type TabItem } from '@/domains/mobile-web/components/TabMobile/TabMobile';
import TopBarHeader from '@/domains/mobile-web/components/TopBarHeader/TopBarHeader';
import { useCartCount } from '@/domains/mobile-web/hooks/useCart';
import {
  useDeleteKidsPost,
  useDeleteKidsPostComment,
  useKidsPostComments,
  useKidsPostPages,
  useLikeKidsPost,
  useLikeKidsPostComment,
} from '@/domains/mobile-web/hooks/useKidsPosts';
import type { CommunityFeed } from '@/domains/mobile-web/types/community.types';
import type { KidsPost } from '@/domains/mobile-web/types/kidsPost.types';
import { toMediaPost } from '@/domains/mobile-web/utils/kidsPost';
import WrapperMobile from '@/shared/components/layout/WrapperMobile/WrapperMobile';
import HeaderMobile from '@/shared/components/ui/HeaderMobile/HeaderMobile';

const TABS: TabItem<CommunityFeed>[] = [
  { value: 'timeline', label: 'Timeline' },
  { value: 'mine', label: 'Postingan Saya' },
];

/** What the open delete confirmation is about */
type PendingDelete = { type: 'post' } | { type: 'comment'; commentId: string };

/** One photo post; its comments are loaded when the comment count is tapped. */
const MediaPostItem = ({ row }: { row: KidsPost }) => {
  const navigate = useNavigate();
  const [commentsOpen, setCommentsOpen] = useState(false);
  const [pendingDelete, setPendingDelete] = useState<PendingDelete | null>(null);
  const comments = useKidsPostComments(row.id, commentsOpen && row.comment_count > 0);
  const likePost = useLikeKidsPost();
  const deletePost = useDeleteKidsPost();
  const likeComment = useLikeKidsPostComment();
  const deleteComment = useDeleteKidsPostComment();

  const commentRows = comments.data?.pages.flatMap((page) => page.rows) ?? [];
  const post = toMediaPost(row, commentRows);
  const commentsNote = comments.isError
    ? 'Gagal memuat komentar.'
    : commentsOpen && row.comment_count > 0 && comments.isLoading
      ? 'Memuat komentar...'
      : undefined;

  const closeDelete = () => setPendingDelete(null);
  const confirmDelete = () => {
    if (pendingDelete?.type === 'post') {
      deletePost.mutate(row.id, { onSettled: closeDelete });
    } else if (pendingDelete?.type === 'comment') {
      deleteComment.mutate(
        { postId: row.id, commentId: pendingDelete.commentId },
        { onSettled: closeDelete }
      );
    }
  };

  return (
    <>
      <CardDonationPost
        post={post}
        showMedia
        onOpen={() => navigate(`/store/request/${row.id}`, { state: { from: 'media', post: row } })}
        showComments={commentsOpen}
        commentsNote={commentsNote}
        onToggleComments={() => setCommentsOpen((open) => !open)}
        onLike={() => likePost.mutate({ postId: row.id, liked: !row.liked_by_me })}
        onReply={() => navigate(`/store/reply/${row.id}`, { state: { from: 'media', post: row } })}
        onDelete={row.is_mine ? () => setPendingDelete({ type: 'post' }) : undefined}
        onLikeComment={(comment) =>
          likeComment.mutate({ postId: row.id, commentId: comment.id, liked: !comment.isLiked })
        }
        onDeleteComment={(comment) => setPendingDelete({ type: 'comment', commentId: comment.id })}
      />
      <ConfirmModal
        open={pendingDelete !== null}
        title={pendingDelete?.type === 'comment' ? 'Hapus Komentar ?' : 'Hapus Postingan ?'}
        message={
          pendingDelete?.type === 'comment'
            ? 'Apakah Anda yakin ingin menghapus komentar ini?'
            : 'Apakah Anda yakin ingin menghapus postingan ini beserta fotonya?'
        }
        confirmLabel="Ya, Hapus"
        variant="danger"
        loading={deletePost.isPending || deleteComment.isPending}
        onConfirm={confirmDelete}
        onClose={closeDelete}
      />
    </>
  );
};

const Media = () => {
  const navigate = useNavigate();
  const cartCount = useCartCount();
  const [activeTab, setActiveTab] = useState<CommunityFeed>('timeline');

  const { data, isLoading, isError, hasNextPage, isFetchingNextPage, fetchNextPage } =
    useKidsPostPages(activeTab);
  const rows = data?.pages.flatMap((page) => page.rows) ?? [];

  // Loads the next page when the end of the list scrolls into view
  const sentinelRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    const node = sentinelRef.current;
    if (!node || !hasNextPage || typeof IntersectionObserver === 'undefined') return;
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0]?.isIntersecting && !isFetchingNextPage) fetchNextPage();
      },
      { rootMargin: '200px' }
    );
    observer.observe(node);
    return () => observer.disconnect();
  }, [hasNextPage, isFetchingNextPage, fetchNextPage]);

  return (
    <main className="min-h-screen relative">
      <HeaderMobile>
        <TopBarHeader
          type="textBucket"
          totalProduct={cartCount}
          title="Media (Foto Anak)"
          onAction={() => navigate('/store/cart')}
        />
        <TabMobile items={TABS} value={activeTab} onChange={setActiveTab} />
      </HeaderMobile>

      <WrapperMobile className="mb-[60px] pt-3 relative">
        <div className="flex flex-col gap-3">
          {isLoading ? (
            <p className="reg12 text-text-grey text-center py-6">Memuat postingan...</p>
          ) : isError ? (
            <p className="reg12 text-text-grey text-center py-6">Gagal memuat postingan.</p>
          ) : rows.length === 0 ? (
            <p className="reg12 text-text-grey text-center py-6">Belum ada postingan.</p>
          ) : (
            <>
              {rows.map((row) => (
                <MediaPostItem key={row.id} row={row} />
              ))}

              {hasNextPage && <div ref={sentinelRef} aria-hidden="true" />}
            </>
          )}
        </div>
        <ButtonFloat onClick={() => navigate('/store/post', { state: { from: 'media' } })} />
      </WrapperMobile>
    </main>
  );
};

export default Media;
